import React from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  X,
  Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MOVE_QUALITY } from '@/engine/moveQuality';

const GameReview = () => {
  const { 
    reviewMode,
    reviewMoveIndex,
    history,
    moveAnalysis,
    playerColor,
    boardTheme,
    exitReviewMode,
    nextReviewMove,
    prevReviewMove,
    firstReviewMove,
    lastReviewMove,
    goToMove
  } = useGameStore();
  
  if (!reviewMode) return null;
  
  const currentAnalysis = moveAnalysis[reviewMoveIndex];
  const isPlayerMove = playerColor === 'white' 
    ? reviewMoveIndex % 2 === 0 
    : reviewMoveIndex % 2 === 1;
  
  // Count move quality stats
  const stats = moveAnalysis.reduce((acc, m) => {
    if (m.quality) {
      acc[m.quality.quality] = (acc[m.quality.quality] || 0) + 1;
    }
    return acc;
  }, {});
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={`
        rounded-xl overflow-hidden
        ${boardTheme === 'cosmic' 
          ? 'bg-slate-900/95 border border-purple-500/30' 
          : 'bg-white border border-gray-200'
        }
      `}
      data-testid="game-review-panel"
    >
      {/* Header */}
      <div className={`
        px-4 py-3 flex items-center justify-between
        ${boardTheme === 'cosmic' ? 'bg-purple-900/30' : 'bg-gray-100'}
      `}>
        <div className="flex items-center gap-2">
          <Play className={`w-5 h-5 ${boardTheme === 'cosmic' ? 'text-purple-400' : 'text-purple-600'}`} />
          <h3 className={`font-bold ${boardTheme === 'cosmic' ? 'text-purple-400' : 'text-purple-800'}`}>
            Game Review
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={exitReviewMode}
          className={boardTheme === 'cosmic' ? 'text-gray-400 hover:text-white' : ''}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Move Navigation */}
      <div className="p-4 space-y-4">
        {/* Current move display */}
        <div className={`
          p-3 rounded-lg text-center
          ${boardTheme === 'cosmic' ? 'bg-slate-800' : 'bg-gray-50'}
        `}>
          <div className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
            Move {Math.floor(reviewMoveIndex / 2) + 1}{reviewMoveIndex % 2 === 0 ? '.' : '...'}
          </div>
          <div className={`text-2xl font-bold font-mono ${boardTheme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
            {history[reviewMoveIndex]}
          </div>
          <div className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`}>
            {isPlayerMove ? 'Your move' : "Opponent's move"}
          </div>
        </div>
        
        {/* Move quality badge */}
        {currentAnalysis?.quality && isPlayerMove && (
          <div className={`
            p-3 rounded-lg
            ${currentAnalysis.quality.bgColor}
            border ${currentAnalysis.quality.borderColor}
          `}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">{currentAnalysis.quality.icon}</span>
              <span 
                className="font-bold"
                style={{ color: currentAnalysis.quality.color }}
              >
                {currentAnalysis.quality.label}
              </span>
            </div>
            <p className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
              {currentAnalysis.quality.explanation}
            </p>
            {!currentAnalysis.quality.isBestMove && currentAnalysis.quality.suggestedMove && (
              <p className={`text-xs mt-2 ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
                Best was: <span className="font-mono">{currentAnalysis.quality.suggestedMove}</span>
              </p>
            )}
          </div>
        )}
        
        {/* Navigation buttons */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={firstReviewMove}
            disabled={reviewMoveIndex === 0}
            className={boardTheme === 'cosmic' ? 'border-slate-600' : ''}
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={prevReviewMove}
            disabled={reviewMoveIndex === 0}
            className={boardTheme === 'cosmic' ? 'border-slate-600' : ''}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className={`px-4 text-sm ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
            {reviewMoveIndex + 1} / {history.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={nextReviewMove}
            disabled={reviewMoveIndex === history.length - 1}
            className={boardTheme === 'cosmic' ? 'border-slate-600' : ''}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={lastReviewMove}
            disabled={reviewMoveIndex === history.length - 1}
            className={boardTheme === 'cosmic' ? 'border-slate-600' : ''}
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Move list with quality indicators */}
        <div className={`
          max-h-40 overflow-y-auto rounded-lg p-2
          ${boardTheme === 'cosmic' ? 'bg-slate-800' : 'bg-gray-50'}
        `}>
          <div className="flex flex-wrap gap-1">
            {history.map((move, idx) => {
              const analysis = moveAnalysis[idx];
              const quality = analysis?.quality;
              const isCurrentMove = idx === reviewMoveIndex;
              const isPlayer = playerColor === 'white' ? idx % 2 === 0 : idx % 2 === 1;
              
              return (
                <button
                  key={idx}
                  onClick={() => goToMove(idx)}
                  className={`
                    px-2 py-1 rounded text-xs font-mono transition-all
                    ${isCurrentMove 
                      ? boardTheme === 'cosmic'
                        ? 'bg-purple-600 text-white'
                        : 'bg-purple-600 text-white'
                      : boardTheme === 'cosmic'
                        ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  style={isPlayer && quality && !isCurrentMove ? {
                    borderBottom: `2px solid ${quality.color}`
                  } : {}}
                >
                  {idx % 2 === 0 && <span className="text-gray-500 mr-1">{Math.floor(idx/2) + 1}.</span>}
                  {move}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Summary stats */}
        <div className={`
          p-3 rounded-lg
          ${boardTheme === 'cosmic' ? 'bg-slate-800' : 'bg-gray-50'}
        `}>
          <div className={`text-xs font-medium mb-2 ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
            Your Move Quality
          </div>
          <div className="flex flex-wrap gap-2">
            {['brilliant', 'great', 'good', 'book', 'inaccuracy', 'mistake', 'blunder'].map(quality => {
              const count = stats[quality] || 0;
              if (count === 0) return null;
              const q = MOVE_QUALITY[quality];
              return (
                <div 
                  key={quality}
                  className="flex items-center gap-1 text-xs"
                  style={{ color: q.color }}
                >
                  <span>{q.icon}</span>
                  <span>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GameReview;
